import { some, forEach } from 'lodash';
import React from 'react';

import connect from 'components/lib/connect';
import FeatureNotification from './feature_notification';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const reportsFourteenMonthLimitOct2026 = {
  roleFlags: [],
  orgIds: [],
  id: 'reportsFourteenMonthLimitOct2026',
  text: 'Reports will return up to 14 months of data beginning this week',
  subtext:
    'Starting October 14, reports from the API, Insight Builder, and the Gladly UI will only return data from the past 14 months, and any report API call without a startAt or endAt will default to the last 14 months instead of all time. Conversation exports, conversation search, and other APIs aren’t affected, and Gladly Support can help if you need older data.',
  endAt: '2026-10-31T06:59:59.000Z',
};

const signalsAvailableOct2026 = {
  roleFlags: [],
  orgIds: [],
  id: 'signalsAvailableOct2026',
  text: 'Signals is now available to you at no cost',
  subtext:
    'Get started with Signals — no additional fee, subject to fair use. If your usage exceeds our fair-use limits, you’ll have the option to purchase additional usage; otherwise, we may limit access. We’ll always contact you before applying any charges.',
  url: 'https://help.gladly.com/docs/what-is-signals',
  urlText: 'Learn more about Signals',
  endAt: '2027-01-01T07:59:59.000Z',
};

const CURRENT_FEATURES = [reportsFourteenMonthLimitOct2026, signalsAvailableOct2026];

export function FeatureNotificationList({ agentPreferences, organization }) {
  const isFeatureEnabled = useIsFeatureEnabled();

  let featureNotifications = [];
  forEach(CURRENT_FEATURES, featureProps => {
    if (shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization)) {
      featureNotifications.push(<FeatureNotification key={featureProps.id} {...featureProps} />);
    }
  });

  if (!featureNotifications.length) {
    return null;
  }

  return <React.Fragment>{featureNotifications}</React.Fragment>;
}

const FeatureNotificationListContainer = connect(mapStateToProps)(FeatureNotificationList);

function mapStateToProps({ getProvider }) {
  const agentPreferences = getProvider('agentPreferences').get();
  const organization = getProvider('auth').get()?.claims;

  return {
    agentPreferences,
    organization,
  };
}

export function useHasFeaturesToShow(agentPreferences, organization) {
  const isFeatureEnabled = useIsFeatureEnabled();
  return some(CURRENT_FEATURES, featureProps => {
    return shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization);
  });
}

export function shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization) {
  const roleRestricted = featureProps.roleFlags && featureProps.roleFlags.length;
  const restrictedAndHasRole = some(featureProps.roleFlags, roleFlag => isFeatureEnabled(roleFlag));
  const flagRestricted = featureProps.flag;

  const organizationId = organization?.id || organization?.orgId || '';
  const orgRestricted = !!featureProps.orgIds.length;
  const belongsToOrg = some(featureProps.orgIds, orgId => orgId.toLowerCase() === organizationId.toLowerCase());

  const currentTime = new Date();
  const startRestricted = !!featureProps.startAt;
  const afterStart = startRestricted ? currentTime > new Date(featureProps.startAt) : true;
  const endRestricted = !!featureProps.endAt;
  const beforeEnd = endRestricted ? currentTime < new Date(featureProps.endAt) : true;

  return (
    (!orgRestricted || belongsToOrg) &&
    (!roleRestricted || restrictedAndHasRole) &&
    (!flagRestricted || isFeatureEnabled(featureProps.flag)) &&
    afterStart &&
    beforeEnd &&
    !agentPreferences.hasSeenFeatureNotification(featureProps.id)
  );
}

export default FeatureNotificationListContainer;
